/**
 * MagneticButton + MagneticGroup
 *
 * MagneticButton wraps its child and pulls it toward the cursor when
 * the pointer is near — the Awwwards / Locomotive / Active-Theory
 * detail. Subtle alone; the whole page feels alive when the primary
 * CTAs respond.
 *
 * MagneticGroup coordinates a row of MagneticButton siblings so they
 * react to each other: the button closest to the cursor pulls toward
 * it as usual, but the others simultaneously push AWAY from the
 * cursor. Without this, two buttons sitting side-by-side both lean
 * toward the cursor at the seam between them and visually overlap.
 *
 * Usage:
 *   // Single button (unchanged)
 *   <MagneticButton><Link to="/portfolio" className="...">…</Link></MagneticButton>
 *
 *   // Pair / row of buttons that should react to each other
 *   <MagneticGroup className="flex gap-4">
 *     <MagneticButton><a …>Kontakt</a></MagneticButton>
 *     <MagneticButton><a …>Portfolio</a></MagneticButton>
 *   </MagneticGroup>
 *
 * Disabled on touch devices (hover/move don't make sense there).
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';

const MagneticGroupContext = createContext(null);
// Carries the INNER (parallax) springs down to a MagneticLabel so the text
// inside the button can move a touch further than the button box itself —
// the real depth trick (box and text must be separate elements for this).
const MagneticInnerContext = createContext(null);

// Hard cap (px) on how far a button BOX may travel toward the cursor. The
// pull is proportional to cursor distance, so without a cap a button near a
// neighbour could slide far enough to overlap it. Clamping keeps the motion
// controlled and prevents side-by-side CTAs from running into each other.
const MAX_PULL = 12;
const clampPull = (v) => (v < -MAX_PULL ? -MAX_PULL : v > MAX_PULL ? MAX_PULL : v);

/* ──────────────────────────── MagneticGroup ──────────────────────── */

/**
 * Wrap two or more MagneticButton siblings in this component. The
 * group takes over the mouse tracking and drives each button's motion
 * values directly:
 *   - the button whose centre is closest to the cursor pulls toward it
 *     with the standard pull strength;
 *   - every other button is pushed AWAY from the cursor by a smaller
 *     factor, scaled by how strongly the active button is being
 *     pulled. As the cursor leaves the row everything snaps back to 0.
 *
 * className / style are forwarded to the wrapper div so callers don't
 * need an extra layout element on top.
 */
export function MagneticGroup({ children, className = '', style }) {
  // List of registered children: { ref, x, y, strength }.
  const buttonsRef = useRef([]);

  const register = (entry) => {
    buttonsRef.current.push(entry);
    return () => {
      buttonsRef.current = buttonsRef.current.filter((b) => b !== entry);
    };
  };

  // Reach radius — cursor must be within this many px of the active
  // button's centre for siblings to feel any push at all. Beyond it
  // the push factor is 0 so wide layouts stay calm.
  const PUSH_REACH = 220;
  // How hard the non-active siblings get shoved (as a fraction of the cursor
  // delta). Moderate: enough that the neighbour eases out of the way so the
  // two never overlap, but not the old 0.18 that felt like too much shoving.
  const PUSH_BASE  = 0.12;

  const onMove = (e) => {
    const cx = e.clientX;
    const cy = e.clientY;

    const entries = buttonsRef.current
      .filter((b) => b.ref.current)
      .map((b) => {
        const r = b.ref.current.getBoundingClientRect();
        const bx = r.left + r.width / 2;
        const by = r.top + r.height / 2;
        const dx = cx - bx;
        const dy = cy - by;
        return { b, dx, dy, dist: Math.hypot(dx, dy) };
      });

    if (entries.length === 0) return;

    // The button currently in the cursor's gravity well.
    const closest = entries.reduce((a, c) => (a.dist < c.dist ? a : c));
    // Push factor falls off with distance to the active button.
    const reachFactor = Math.max(0, 1 - closest.dist / PUSH_REACH);

    entries.forEach(({ b, dx, dy }) => {
      let ox;
      let oy;
      if (b === closest.b) {
        // Active button pulls toward the cursor at its own strength.
        ox = clampPull(dx * b.strength);
        oy = clampPull(dy * b.strength);
      } else {
        // Siblings push AWAY from the cursor (note the minus). Scale
        // by reachFactor so they relax to 0 when the active button is
        // far from the cursor too.
        ox = clampPull(-dx * PUSH_BASE * reachFactor);
        oy = clampPull(-dy * PUSH_BASE * reachFactor);
      }
      b.x.set(ox);
      b.y.set(oy);
      // Drive the inner label parallax in group mode too — the label leads
      // a touch further than the button, the depth detail (this used to be
      // standalone-only, which is why grouped CTAs felt flatter).
      b.ix.set(ox * b.innerStrength);
      b.iy.set(oy * b.innerStrength);
    });
  };

  const reset = () => {
    buttonsRef.current.forEach((b) => {
      b.x.set(0);
      b.y.set(0);
      b.ix.set(0);
      b.iy.set(0);
    });
  };

  return (
    <MagneticGroupContext.Provider value={{ register }}>
      <div
        onMouseMove={onMove}
        onMouseLeave={reset}
        className={className}
        style={style}
      >
        {children}
      </div>
    </MagneticGroupContext.Provider>
  );
}

/* ──────────────────────────── MagneticButton ─────────────────────── */

export default function MagneticButton({
  children,
  strength = 0.4,         // 0 = none, 0.5 = strong pull (0.4 = the premium sweet spot)
  innerStrength = 0.72,   // label leads further than the wrapper → visible parallax depth
  className = '',
  // When the child is a w-full button (e.g. the cart Checkout CTA),
  // set fullWidth so the wrapper itself spans the parent — otherwise
  // the inline-block wrapper collapses to the button's content width
  // and a w-full child can't actually expand. Default stays
  // inline-block so side-by-side CTAs still pack horizontally.
  fullWidth = false,
  ...props
}) {
  const ref = useRef(null);

  // Outer (wrapper) motion values — driven by either the local
  // onMouseMove handler OR by the parent MagneticGroup. Both write to
  // the same useMotionValue, useSpring smooths the visual result.
  // Outer spring is now under-damped (ζ≈0.56) so it OVERSHOOTS and settles
  // with a satisfying elastic wobble on release — the "premium" feel of
  // GSAP's elastic.out, instead of the old near-critically-damped snap.
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 190, damping: 12, mass: 0.6 });
  const sy = useSpring(y, { stiffness: 190, damping: 12, mass: 0.6 });

  // Inner motion (the label leads — moves a touch further than the wrapper,
  // the parallax depth trick). Kept a little tighter/faster than the outer
  // so it leads cleanly while still carrying a hint of the same bounce.
  const ix = useMotionValue(0);
  const iy = useMotionValue(0);
  const six = useSpring(ix, { stiffness: 260, damping: 16, mass: 0.45 });
  const siy = useSpring(iy, { stiffness: 260, damping: 16, mass: 0.45 });

  const groupCtx = useContext(MagneticGroupContext);

  // Register with the group if present — the group needs access to
  // our motion values + strength so it can drive them directly.
  useEffect(() => {
    if (!groupCtx) return undefined;
    return groupCtx.register({ ref, x, y, ix, iy, strength, innerStrength });
  }, [groupCtx, x, y, ix, iy, strength, innerStrength]);

  // Skip on touch — hover/move don't fire predictably.
  const isTouch =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(hover: none)').matches;
  if (isTouch) return <>{children}</>;

  // Standalone-mode handlers (group handles its own tracking).
  const onMove = groupCtx
    ? undefined
    : (e) => {
        if (!ref.current) return;
        const r = ref.current.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const dx = e.clientX - cx;
        const dy = e.clientY - cy;
        // Clamp the BOX travel so a long cursor approach can't slide the
        // button across its gap into a neighbour.
        const ox = clampPull(dx * strength);
        const oy = clampPull(dy * strength);
        x.set(ox);
        y.set(oy);
        // Label leads the box by innerStrength (derived from the CLAMPED box
        // offset, so the text stays bounded too — it shifts a few px further
        // than the box for the parallax depth but never spills out).
        ix.set(ox * innerStrength);
        iy.set(oy * innerStrength);
      };

  const reset = groupCtx
    ? undefined
    : () => {
        x.set(0); y.set(0);
        ix.set(0); iy.set(0);
      };

  const wrapperDisplay = fullWidth ? 'block' : 'inline-block';
  const wrapperWidth   = fullWidth ? '100%'  : undefined;

  // The outer wrapper moves the whole button (box + text) by the base pull.
  // The inner springs (six/siy) are handed to any <MagneticLabel> inside via
  // context so the TEXT can move a touch further → visible box-vs-text
  // parallax. Without a MagneticLabel the button simply moves as one piece.
  return (
    <MagneticInnerContext.Provider value={{ x: six, y: siy }}>
      <motion.div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={reset}
        style={{ x: sx, y: sy, display: wrapperDisplay, width: wrapperWidth }}
        className={className}
        {...props}
      >
        {children}
      </motion.div>
    </MagneticInnerContext.Provider>
  );
}

/* ──────────────────────────── MagneticLabel ─────────────────────── */

/**
 * Wrap the TEXT inside a magnetic button in this so it leads the box by the
 * parallax amount (the premium depth detail). Falls back to a plain inline
 * span when used outside a MagneticButton (or on touch, where there's no
 * inner context).
 *
 *   <MagneticButton>
 *     <a className="btn"><MagneticLabel>Let's talk</MagneticLabel></a>
 *   </MagneticButton>
 */
export function MagneticLabel({ children, className = '', style, ...props }) {
  const inner = useContext(MagneticInnerContext);
  if (!inner) return <span className={className} style={style} {...props}>{children}</span>;
  return (
    <motion.span
      // Default to inline-block, but let callers override `display` (e.g.
      // inline-flex when the label holds an icon + text that must sit in a
      // row). x/y come last so the motion values can't be clobbered.
      style={{ display: 'inline-block', willChange: 'transform', ...style, x: inner.x, y: inner.y }}
      className={className}
      {...props}
    >
      {children}
    </motion.span>
  );
}
